import { Injectable, inject, signal, computed, DestroyRef, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { Subject, timer } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { ToastService as MessageService } from '@shared/components/ui/toast/toast.service';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { SoundService } from './sound.service';
import { AppStorageService } from './app-storage.service';
import { NotificationPreferencesService } from '../../features/settings/services/notification-preferences.service';

// GAP-FU-02 · 'danger' is an alias for 'error' (design-system v1.1
// naming) and 'neutral' is a low-key informational variant for non-
// alert system messages.
export type NotificationSeverity =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'danger'
  | 'neutral'
  | 'critical';
export type NotificationCategory =
  | 'trade'
  | 'order'
  | 'position'
  | 'risk'
  | 'market'
  | 'system'
  | 'compliance'
  | 'connection';

export interface NotificationConfig {
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  duration?: number;
  closable?: boolean;
  category?: NotificationCategory;
  actionable?: boolean;
  actions?: NotificationAction[];
  metadata?: Record<string, unknown>;
}

export interface TradingAlert {
  id: string;
  title: string;
  message: string;
  severity: NotificationSeverity;
  category: NotificationCategory;
  timestamp: Date;
  read: boolean;
  sticky: boolean;
  actionable: boolean;
  actions?: NotificationAction[];
  metadata?: Record<string, unknown>;
  source?: string;
}

export interface NotificationAction {
  label: string;
  action: string;
  data?: unknown;
  style?: 'primary' | 'secondary' | 'danger';
}

export interface NotificationSettings {
  enableSound: boolean;
  enableDesktop: boolean;
  enableToast: boolean;
  soundVolume: number;
  autoHideDelay: number;
  priorities: Record<NotificationCategory, number>;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byCategory: Record<NotificationCategory, number>;
  bySeverity: Record<NotificationSeverity, number>;
  todayCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly messageService = inject(MessageService);
  // WS-DEDUP (HAR 2026-07-01): use the shared, ref-counted WebSocketService
  // (Service A) that ALL other /ws/trading consumers use, instead of the second
  // WebSocketManagerService singleton — which opened a duplicate trading socket
  // (the only endpoint that was connected through both managers).
  private readonly wsService = inject(WebSocketService);
  private readonly authService = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly soundService = inject(SoundService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStorage = inject(AppStorageService);
  private readonly preferences = inject(NotificationPreferencesService);
  private readonly actionSubject = new Subject<{ alert: TradingAlert; action: NotificationAction }>();

  // State signals
  private readonly alerts = signal<TradingAlert[]>([]);
  private readonly settings = signal<NotificationSettings>(this.getDefaultSettings());
  readonly currentSettings = computed(() => ({ ...this.settings(), ...this.preferences.presentation() }));
  readonly settingsStatus = this.preferences.presentationStatus;
  readonly settingsReady = this.preferences.presentationReady;
  private readonly desktopPermission = signal<NotificationPermission>('default');

  // Audio — delegated to SoundService (GAP-FU-13-CONSOLIDATE).
  // The previous inline `audioContext` + `alertSound` fields and the
  // `initializeAudio()` + `playAlertSound()` methods were removed; the
  // playback primitive now lives in SoundService.playRaw('alert', v).
  // NotificationService still owns its own preference gates (enableSound,
  // severity check) and only delegates the audio primitive.

  // Computed values
  readonly unreadCount = computed(() =>
    this.alerts().filter(a => !a.read).length
  );

  readonly criticalAlerts = computed(() =>
    this.alerts().filter(a => a.severity === 'critical' && !a.read)
  );

  readonly recentAlerts = computed(() =>
    this.alerts().slice(0, 10)
  );

  readonly stats = computed((): NotificationStats => {
    const allAlerts = this.alerts();
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const stats: NotificationStats = {
      total: allAlerts.length,
      unread: this.unreadCount(),
      byCategory: {} as Record<NotificationCategory, number>,
      bySeverity: {} as Record<NotificationSeverity, number>,
      todayCount: 0
    };

    allAlerts.forEach(alert => {
      stats.byCategory[alert.category] = (stats.byCategory[alert.category] || 0) + 1;
      stats.bySeverity[alert.severity] = (stats.bySeverity[alert.severity] || 0) + 1;
      if (alert.timestamp >= todayStart) {
        stats.todayCount++;
      }
    });

    return stats;
  });

  // Public observables
  readonly action$ = this.actionSubject.asObservable();

  constructor() {
    // GAP-FU-13-CONSOLIDATE: initializeAudio() removed — SoundService owns
    // the AudioContext lifecycle and lazily initialises it on first
    // playRaw() call.
    this.requestDesktopPermission();
    this.setupWebSocketListener();
    this.loadStoredAlerts();
    this.setupAutoCleanup();
  }

  // Legacy methods for backward compatibility
  show(config: NotificationConfig): void {
    this.messageService.add({
      severity: config.type,
      summary: config.title,
      detail: config.message,
      life: config.duration || 5000,
      closable: config.closable !== false
    });

    // Also add as trading alert if category specified
    if (config.category) {
      this.addAlert({
        title: config.title,
        message: config.message,
        severity: config.type === 'error' ? 'error' : config.type as NotificationSeverity,
        category: config.category,
        sticky: false,
        actionable: config.actionable || false,
        actions: config.actions,
        metadata: config.metadata
      });
    }
  }

  showSuccess(title: string, message: string): void {
    this.show({ type: 'success', title, message });
  }

  showError(title: string, message: string): void {
    this.show({ type: 'error', title, message });
  }

  showWarning(title: string, message: string): void {
    this.show({ type: 'warning', title, message });
  }

  showInfo(title: string, message: string): void {
    this.show({ type: 'info', title, message });
  }

  clear(): void {
    this.messageService.clear();
  }

  // New trading alert methods

  /**
   * Add a trading alert
   */
  addAlert(alert: Omit<TradingAlert, 'id' | 'timestamp' | 'read'>): void {
    const newAlert: TradingAlert = {
      ...alert,
      id: this.generateId(),
      timestamp: new Date(),
      read: false
    };

    this.alerts.update(alerts => [newAlert, ...alerts].slice(0, 100));
    this.displayAlert(newAlert);
    this.storeAlerts();
  }

  /**
   * Mark alert as read
   */
  markAsRead(alertId: string): void {
    this.alerts.update(alerts =>
      alerts.map(a => a.id === alertId ? { ...a, read: true } : a)
    );
    this.storeAlerts();
  }

  /**
   * Mark all alerts as read
   */
  markAllAsRead(): void {
    this.alerts.update(alerts => alerts.map(a => ({ ...a, read: true })));
    this.storeAlerts();
  }

  /**
   * Remove an alert
   */
  removeAlert(alertId: string): void {
    this.alerts.update(alerts => alerts.filter(a => a.id !== alertId));
    this.storeAlerts();
  }

  /**
   * Clear all alerts
   */
  clearAllAlerts(): void {
    this.alerts.set([]);
    // GAP-FE-C013: SSR-safe wrapper; preserves the 'tradingAlerts' key.
    this.appStorage.removeItem('tradingAlerts');
  }

  /**
   * Execute alert action
   */
  executeAction(alert: TradingAlert, action: NotificationAction): void {
    this.actionSubject.next({ alert, action });
    this.markAsRead(alert.id);
  }

  /**
   * Update settings
   */
  updateSettings(settings: Partial<NotificationSettings>): void {
    this.settings.update(current => ({ ...current, ...settings }));
    const { enableSound, enableDesktop, enableToast, soundVolume } = settings;
    this.preferences.updatePresentation({
      ...(enableSound !== undefined ? { enableSound } : {}),
      ...(enableDesktop !== undefined ? { enableDesktop } : {}),
      ...(enableToast !== undefined ? { enableToast } : {}),
      ...(soundVolume !== undefined ? { soundVolume } : {})
    });
  }

  retrySettings(): void {
    this.preferences.retryPresentation();
  }

  /**
   * Toggle sound
   */
  toggleSound(): void {
    this.updateSettings({ enableSound: !this.currentSettings().enableSound });
  }

  /**
   * Create alert from WebSocket message.
   *
   * Inbound shape varies by `message.type` discriminator; the structural
   * type below captures every field actually read across the switch
   * branches (order_filled / position_liquidation_warning /
   * risk_limit_breach / default).
   */
  createAlertFromWebSocket(rawMessage: unknown): void {
    const message = rawMessage as {
      type: string;
      data: Record<string, unknown> & {
        side?: string;
        quantity?: number;
        symbol?: string;
        price?: number;
        orderId?: string;
        message?: string;
      };
    };
    let alert: Omit<TradingAlert, 'id' | 'timestamp' | 'read'>;

    switch (message.type) {
      case 'order_filled':
        alert = {
          title: 'Order Filled',
          message: `${message.data.side} ${message.data.quantity} ${message.data.symbol} @ ${message.data.price}`,
          severity: 'success',
          category: 'order',
          sticky: false,
          actionable: true,
          actions: [
            { label: 'View Order', action: 'view_order', data: message.data.orderId }
          ],
          metadata: message.data
        };
        break;

      case 'position_liquidation_warning':
        alert = {
          title: 'Liquidation Warning',
          message: `Position ${message.data.symbol} approaching liquidation`,
          severity: 'critical',
          category: 'risk',
          sticky: true,
          actionable: true,
          actions: [
            { label: 'Close Position', action: 'close_position', data: message.data, style: 'danger' }
          ],
          metadata: message.data
        };
        break;

      case 'risk_limit_breach':
        alert = {
          title: 'Risk Limit Breach',
          message: message.data.message ?? '',
          severity: 'warning',
          category: 'risk',
          sticky: true,
          actionable: false,
          metadata: message.data
        };
        break;

      default:
        alert = {
          title: message.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
          message: message.data.message || JSON.stringify(message.data),
          severity: 'info',
          category: 'system',
          sticky: false,
          actionable: false,
          metadata: message.data
        };
    }

    this.addAlert(alert);
  }

  // Private methods

  private getDefaultSettings(): NotificationSettings {
    return {
      enableSound: true,
      enableDesktop: true,
      enableToast: true,
      soundVolume: 0.5,
      autoHideDelay: 5000,
      priorities: {
        trade: 10,
        order: 9,
        position: 8,
        risk: 10,
        market: 7,
        system: 5,
        compliance: 9,
        connection: 6
      }
    };
  }

  private displayAlert(alert: TradingAlert): void {
    const settings = this.currentSettings();

    // Show toast — map v1.1 names ('danger', 'neutral') to MessageService
    // primitives ('error', 'info'), and collapse 'critical' to 'error'.
    const toastSeverity: 'info' | 'warning' | 'error' | 'success' = (() => {
      switch (alert.severity) {
        case 'critical':
        case 'danger':
          return 'error';
        case 'neutral':
          return 'info';
        default:
          return alert.severity;
      }
    })();
    if (settings.enableToast) {
      this.messageService.add({
        severity: toastSeverity,
        summary: alert.title,
        detail: alert.message,
        life: alert.sticky ? undefined : settings.autoHideDelay,
        sticky: alert.sticky
      });
    }

    // Play sound (GAP-FU-13-CONSOLIDATE: delegated to SoundService).
    // NotificationService keeps ownership of the preference gates; only the
    // audio-playback primitive is delegated. Volume math:
    //   legacy peak gain = settings.soundVolume * 0.1
    //   SoundService.playRaw internal envelope peaks at volume * 0.4
    //   so to preserve the legacy peak we pass settings.soundVolume * 0.25
    //   (0.25 * 0.4 = 0.1).
    if (settings.enableSound &&
        (alert.severity === 'critical' || alert.severity === 'error' || alert.severity === 'warning')) {
      this.soundService.playRaw('alert', settings.soundVolume * 0.25);
    }

    // Show desktop notification
    if (settings.enableDesktop && this.desktopPermission() === 'granted' && !this.document.hasFocus()) {
      this.showDesktopNotification(alert);
    }
  }

  private showDesktopNotification(alert: TradingAlert): void {
    const win = this.document.defaultView;
    if (!win || !('Notification' in win)) {return;}

    const notification = new Notification(alert.title, {
      body: alert.message,
      icon: 'assets/icons/notification.svg',
      tag: alert.id,
      requireInteraction: alert.sticky
    });

    notification.onclick = () => {
      win.focus();
      this.markAsRead(alert.id);
    };

    if (!alert.sticky) {
      setTimeout(() => notification.close(), this.settings().autoHideDelay);
    }
  }

  // GAP-FU-13-CONSOLIDATE: `initializeAudio()` and `playAlertSound()`
  // were removed. The AudioContext lifecycle and oscillator playback are
  // now owned by `SoundService`; callers delegate via `playRaw(cue, v)`.

  private async requestDesktopPermission(): Promise<void> {
    const win = this.document.defaultView;
    if (win && 'Notification' in win && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        this.desktopPermission.set(permission);
      } catch (error) {
        this.logger.error('Failed to request notification permission:', { component: 'NotificationService' }, error);
      }
    }
  }

  private setupWebSocketListener(): void {
    // RE-ENABLED (post DEV-1434): WebSocket connects only after authentication is confirmed.
    // isAuthenticated$ emits true when the user is set (via stored session or login).
    // If initializeSession() fails, handleLogout() clears auth state and isAuthenticated$
    // emits false, which causes switchMap to unsubscribe from the WS connection.
    this.authService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter(isAuth => isAuth),
        switchMap(() => this.wsService.connect('trading')),
        filter(message => this.isAlertMessage(message)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(message => {
        this.createAlertFromWebSocket(message);
      });
  }

  // GAP-FE-A01 r123 (Opus-Max v92): `message: any` → `{ type?: string }`.
  // The type-discriminator check only needs to read `message.type`; the
  // rest of the payload is irrelevant here. Narrowing to the minimal
  // shape removes the `any` without needing the full WebSocketMessage<T>
  // discriminated-union refactor flagged by the gap text (deferred to
  // a future round).
  private isAlertMessage(message: { type?: string }): boolean {
    const alertTypes = [
      'order_filled', 'order_rejected', 'position_liquidation_warning',
      'risk_limit_breach', 'market_halt', 'connection_lost'
    ];
    return typeof message.type === 'string' && alertTypes.includes(message.type);
  }

  private setupAutoCleanup(): void {
    timer(60000, 60000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
        this.alerts.update(alerts =>
          alerts.filter(a => a.sticky || a.timestamp > cutoff)
        );
        this.storeAlerts();
      });
  }

  private loadStoredAlerts(): void {
    // GAP-FE-C013: SSR-safe wrapper. Raw getItem (not getJSON) because the parsed
    // array needs a custom timestamp string→Date restore below; preserves the
    // 'tradingAlerts' key and existing parse semantics.
    const stored = this.appStorage.getItem('tradingAlerts');
    if (stored) {
      try {
        const alerts = JSON.parse(stored);
        // GAP-FE-A01 r123 (Opus-Max v92): `(a: any)` → typed intersection.
        // The parsed array is `localStorage` JSON we serialized ourselves
        // — known to match TradingAlert except `timestamp` is a string
        // post-JSON.stringify, restored to Date here.
        this.alerts.set(
          alerts.map((a: TradingAlert & { timestamp: string }) => ({
            ...a,
            timestamp: new Date(a.timestamp)
          }))
        );
      } catch (error) {
        this.logger.error('Failed to load alerts:', { component: 'NotificationService' }, error);
      }
    }
  }

  private storeAlerts(): void {
    const toStore = this.alerts()
      .slice(0, 50)
      .map(a => ({
        ...a,
        timestamp: a.timestamp.toISOString()
      }));
    // GAP-FE-C013: SSR-safe wrapper; preserves the 'tradingAlerts' key and the
    // pre-serialized ISO-timestamp shape.
    this.appStorage.setJSON('tradingAlerts', toStore);
  }

  private generateId(): string {
    return `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  ngOnDestroy(): void {
    // GAP-FU-13-CONSOLIDATE: AudioContext lifecycle is owned by
    // SoundService (via `destroyRef.onDestroy(() => ctx.close())`).
    // No-op kept to preserve the OnDestroy contract for any subclass /
    // future cleanup the service may need.
  }
}
